

import { useEffect, useState } from 'react';
import { Loader2, PackageOpen } from 'lucide-react';
import { getSportConfig } from '@/config/sports';
import type { Sport } from '@prisma/client';
import { API_BASE } from '@/lib/api';
import { PackCard } from './PackCard';

/** A pack as served by GET /api/packs (NBA and NFL shapes unified). */
export interface StorePack {
  id: string;
  name: string;
  description: string;
  sport?: string;
  price: number;
  priceType: 'USD' | 'COINS' | 'GEMS';
  coinPrice?: number;
  usdPrice?: number;
  cardCount: number;
  tier?: string;
  features: string[];
  positionGuarantees?: string[];
  /** NFL odds disclosure: exact per-fill-slot pull rates by display class. */
  pullRates?: { common: number; rare: number; legendary: number };
  /** NFL guaranteed Rare+ slots and their Legendary upgrade rates. */
  rarityGuarantees?: { slot: string; minClass: string; legendaryUpgradeRate: number }[];
  guaranteedRarity?: string | null;
  allRarePlus?: boolean;
  /** Dynamic series-supply sold-out (guaranteed NFL SKUs). SKU stays listed. */
  soldOut?: boolean;
  /** 'SERIES_SUPPLY' when soldOut; distinct from permanent unavailability. */
  soldOutReason?: string | null;
}

interface PackGridProps {
  /** Sport catalog to fetch (?sport=NFL). */
  sport: string;
  onPurchase: (pack: StorePack) => void;
  loadingPackId?: string | null;
}

/** Footer series line from the sport identity ("NFL · RUBY SERIES · 2026"). */
function seriesTextFor(sport: string): string | undefined {
  const config = getSportConfig(sport as Sport);
  if (!config) return undefined;
  return `${config.shortLabel} · ${config.identity.metal.toUpperCase()} SERIES · ${new Date().getFullYear()}`;
}

export function PackGrid({ sport, onPurchase, loadingPackId }: PackGridProps) {
  const [packs, setPacks] = useState<StorePack[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/packs?sport=${encodeURIComponent(sport)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPacks(data.packs || []);
      } catch {
        if (!cancelled) setPacks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [sport]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-gold animate-spin" />
      </div>
    );
  }

  if (packs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <PackageOpen className="h-10 w-10 text-tertiary mb-3" />
        <p className="text-warm-gray">No packs available right now</p>
      </div>
    );
  }

  const seriesText = seriesTextFor(sport);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {packs.map((pack) => (
        <PackCard
          key={pack.id}
          id={pack.id}
          name={pack.name}
          description={pack.description}
          price={pack.price}
          priceType={pack.priceType}
          cardCount={pack.cardCount}
          features={pack.features}
          guaranteedRarity={pack.guaranteedRarity ?? null}
          allRarePlus={pack.allRarePlus ?? false}
          soldOut={pack.soldOut ?? false}
          seriesText={seriesText}
          onPurchase={() => onPurchase(pack)}
          isLoading={loadingPackId === pack.id}
        />
      ))}
    </div>
  );
}
